import axiosInstance from '../utils/axios';

/**
 * 지문관리 서비스 모듈
 * 
 * 새로운 MVC 아키텍처의 Fingerprint API와 통신하는 서비스 레이어
 */
class FingerprintService {
    constructor() {
        this.baseURL = '/fingerprint';
    }

    /**
     * 지문 목록 조회
     * @param {number} page - 페이지 번호 (1부터 시작)
     * @param {number} pageSize - 페이지 크기
     * @param {number} searchKind - 검색 종류 (0: 전체, 1: 사용자명, 2: 사용자ID, 3: 이메일, 4: 제조사, 5: 모델명, 6: 시리얼번호)
     * @param {string} keyword - 검색 키워드
     * @returns {Promise<Object>} 지문 목록 응답
     */
    async getFingerprintList(page = 1, pageSize = 10, searchKind = 0, keyword = '') {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/list`, {
                params: {
                    page,
                    pageSize,
                    searchKind,
                    keyword
                }
            });

            return response.data;
        } catch (error) {
            console.error('getFingerprintList error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 지문 검색 자동완성
     * @param {number} searchKind - 검색 종류
     * @param {string} keyword - 검색 키워드
     * @param {number} limit - 결과 개수 제한 (기본값: 10)
     * @returns {Promise<Object>} 자동완성 결과
     */
    async getSearchSuggest(searchKind = 0, keyword = '', limit = 10) {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/search-suggest`, {
                params: {
                    searchKind,
                    keyword,
                    limit
                }
            });

            return response.data;
        } catch (error) {
            console.error('getSearchSuggest error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 지문 상세 정보 조회
     * @param {number} userIdx - 사용자 ID
     * @param {string} deviceId - 디바이스 ID
     * @returns {Promise<Object>} 지문 상세 정보
     */
    async getFingerprintDetail(userIdx, deviceId) {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/${userIdx}/${deviceId}`);
            return response.data;
        } catch (error) {
            console.error('getFingerprintDetail error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 사용자별 지문 목록 조회
     * @param {number} userIdx - 사용자 ID
     * @returns {Promise<Object>} 사용자별 지문 목록
     */
    async getUserFingerprintList(userIdx) {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/user/${userIdx}`);
            return response.data;
        } catch (error) {
            console.error('getUserFingerprintList error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 지문 생성
     * @param {Object} fingerprintData - 지문 데이터
     * @returns {Promise<Object>} 생성 결과
     */
    async createFingerprint(fingerprintData) {
        try {
            const response = await axiosInstance.post(`${this.baseURL}`, fingerprintData);
            return response.data;
        } catch (error) {
            console.error('createFingerprint error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 지문 수정
     * @param {number} userIdx - 사용자 ID
     * @param {string} deviceId - 디바이스 ID
     * @param {Object} fingerprintData - 수정할 지문 데이터
     * @returns {Promise<Object>} 수정 결과
     */
    async updateFingerprint(userIdx, deviceId, fingerprintData) {
        try {
            const response = await axiosInstance.put(`${this.baseURL}/${userIdx}/${deviceId}`, fingerprintData);
            return response.data;
        } catch (error) {
            console.error('updateFingerprint error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 지문 삭제
     * @param {number} userIdx - 사용자 ID
     * @param {string} deviceId - 디바이스 ID
     * @returns {Promise<Object>} 삭제 결과
     */
    async deleteFingerprint(userIdx, deviceId) {
        try {
            const response = await axiosInstance.delete(`${this.baseURL}/${userIdx}/${deviceId}`);
            return response.data;
        } catch (error) {
            console.error('deleteFingerprint error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 사용자 전체 지문 삭제
     * @param {number} userIdx - 사용자 ID
     * @returns {Promise<Object>} 삭제 결과
     */
    async deleteUserFingerprints(userIdx) {
        try {
            const response = await axiosInstance.delete(`${this.baseURL}/user/${userIdx}`);
            return response.data;
        } catch (error) {
            console.error('deleteUserFingerprints error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 지문 엑셀 다운로드
     * @param {number} searchKind - 검색 종류
     * @param {string} keyword - 검색 키워드
     * @returns {Promise<Blob>} 엑셀 파일 데이터
     */
    async getFingerprintExcelData(searchKind = 0, keyword = '') {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/excel`, {
                params: {
                    searchKind,
                    keyword
                },
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            console.error('getFingerprintExcelData error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 지문 통계 조회
     * @returns {Promise<Object>} 지문 통계 데이터
     */
    async getFingerprintStatistics() {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/statistics`);
            return response.data;
        } catch (error) {
            console.error('getFingerprintStatistics error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 디바이스별 지문 조회
     * @param {string} deviceId - 디바이스 ID
     * @returns {Promise<Object>} 디바이스별 지문 정보
     */
    async getFingerprintByDevice(deviceId) {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/device/${deviceId}`);
            return response.data;
        } catch (error) {
            console.error('getFingerprintByDevice error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 에러 처리 헬퍼 메서드
     * @param {Error} error - 에러 객체
     * @returns {Error} 처리된 에러
     */
    handleError(error) {
        if (error.response) {
            // 서버 응답이 있는 경우
            const { status, data } = error.response;
            const message = data?.message || `HTTP ${status} 오류가 발생했습니다.`;
            return new Error(message);
        } else if (error.request) {
            // 요청이 전송되었지만 응답이 없는 경우
            return new Error('서버와의 통신에 실패했습니다.');
        } else {
            // 요청 설정 중 오류가 발생한 경우
            return new Error(error.message || '알 수 없는 오류가 발생했습니다.');
        }
    }
}

// 싱글톤 인스턴스 생성 및 내보내기
const fingerprintService = new FingerprintService();
export default fingerprintService;
